import { Empty } from "./Empty.tsx";

/**
 * Поиск по списку: поле с лупой в полосе контролов над таблицей.
 *
 * ⚠️ **Новый компонент: до 14.09.2026 поиска не было НИ НА ОДНОМ экране
 * продукта.** Место, где его ищут глазами, занимала форма заведения (одиночное
 * поле «Имя ученика» с кнопкой «Завести» над списком), и агент, проходивший
 * стенд, принял её за фильтр. Значит спутает и преподаватель.
 *
 * Различаются они тремя признаками сразу, и все три обязательны (решение
 * владельца 3 раздела 2.4 задания на дизайн-систему):
 *
 * 1. заведение больше не поле с кнопкой, а главное действие экрана: залитая
 *    кнопка в правом конце строки заголовка, открывающая окно;
 * 2. поиск стоит в полосе фильтров над таблицей слева, шириной `--w-search`;
 * 3. у поиска лупа слева и подпись «Поиск по имени», а не «Имя ученика».
 *
 * Замок на пару стоит в `lib/switch-kind.test.ts`: экран с поиском не имеет
 * права нести форму заведения над списком.
 *
 * ⚠️ **Отбор идёт по уже загруженному списку** (`lib/search.ts`), своей ручки
 * API у поиска нет. Отсюда и отсутствие задержки на ввод: считать нечего, а
 * задержка в отборе тридцати строк это мигание списка на ровном месте.
 *
 * ⚠️ **`type="search"`, а не `text`**: читалка обязана назвать поле поисковым,
 * иначе она читает «текстовое поле, поиск по имени», и слово «поиск»
 * оказывается частью имени, а не роли. Свой крестик Chrome при этом гасится
 * правилом в `styles.css`, там же записано почему.
 */
export function SearchField(props: {
  /**
   * Подпись поля. ⚠️ Она же плейсхолдер, она же имя для читалки: три разных
   * текста на один вопрос это ровно тот разнобой, из-за которого форму
   * заведения принимали за поиск. Пишется тем, ЧТО ищут: «Поиск по имени» у
   * учеников, «Поиск по названию» у заготовок и групп.
   */
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="search">
      {/* Лупа это подпись, а не кнопка: щелчок по ней проваливается в поле,
          `pointer-events` снят в styles.css */}
      <svg
        className="search__ico"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="6.5" />
        <path d="M15.8 15.8 20 20" />
      </svg>
      <input
        className="fld"
        type="search"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.label}
        aria-label={props.label}
        maxLength={80}
      />
      {/* Крестик появляется только при набранном запросе: пустое поле очищать
          нечем, а постоянная кнопка отнимает у подписи четверть ширины */}
      {props.value !== "" && (
        <button
          type="button"
          className="search__x"
          aria-label="Очистить поиск"
          onClick={() => props.onChange("")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12" />
            <path d="M18 6 6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Что показывать вместо списка, когда поиск не нашёл ничего.
 *
 * ⚠️ Случай «ничего не нашлось» отличается от «пусто совсем» тем, что у него
 * есть выход, и выход этот один: снять запрос. Поэтому тихая кнопка здесь
 * обязательна, а на пустом списке её нет вовсе (там выход это главное действие
 * экрана).
 *
 * ⚠️ **Своей разметки у этого случая с 14.09.2026 нет**, и держать её второй раз
 * незачем: пустое состояние в продукте рисует одна плашка на три случая
 * (`lib/Empty.tsx`), и «ничего не нашлось» это её второй случай. Здесь осталось
 * ровно то, чего плашка знать не может: какой значок, какие слова и что делает
 * кнопка.
 */
export function NothingFound(props: {
  query: string;
  /**
   * Чего именно нет, в родительном падеже: «ни одного ученика», «ни одной
   * заготовки». Пишется экраном, а не собирается здесь: род и число зависят от
   * слова, а общая формулировка вышла бы «нет ни одной строки», то есть нашим
   * словарём в тексте для чужого.
   */
  nothing: string;
  onClear: () => void;
}) {
  return (
    <Empty
      icon="search"
      title="Ничего не нашлось"
      text={`По запросу «${props.query.trim()}» нет ${props.nothing}.`}
      action={{ label: "Очистить поиск", onClick: props.onClear, quiet: true }}
    />
  );
}
